import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';

const SITE_URL = 'https://luosai123.cn';

export default function GuideDetailPage() {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);

  const jsonLd = useMemo(() => {
    if (!article) {
      return undefined;
    }
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '爆料网上',
          url: `${SITE_URL}/`,
        },
        mainEntityOfPage: `${SITE_URL}/guide/${article.slug}`,
        articleSection: article.category,
        keywords: article.tags.join(','),
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '发布指南', item: `${SITE_URL}/guide` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `${SITE_URL}/guide/${article.slug}` },
        ],
      },
    ];
  }, [article]);

  useSeo({
    title: article ? `${article.title}｜爆料网上发布指南` : '指南未找到｜爆料网上',
    description: article ? article.excerpt : '该指南页面不存在，请返回发布指南列表查看全部内容。',
    keywords: article ? article.tags.join(',') : '爆料指南,爆料网上',
    path: `/guide/${slug || ''}`,
    jsonLd,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">找不到这篇指南</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            它可能已经调整了地址，或者链接输入有误。你可以返回列表浏览全部发布指南。
          </p>
          <Link
            to="/guide"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>返回发布指南
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = articles.filter((item) => item.slug !== article.slug).slice(0, 3);

  return (
    <SiteLayout>
      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '发布指南', to: '/guide' },
                { label: article.title },
              ]}
            />
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-sm bg-accent-100 px-2.5 py-1 text-xs text-accent-800">
              <i className="ri-price-tag-3-line"></i>
              {article.category}
            </span>
            <h1 className="mt-4 max-w-4xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                约 {article.readTime}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.45fr_0.55fr]">
            <div className="min-w-0">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-100 md:h-80">
                <img
                  src={article.cover}
                  alt={`${article.title} 主题配图`}
                  title={`${article.title} - 爆料网上发布指南`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-10 space-y-10">
                {article.content.map((section, index) => (
                  <section key={section.heading} id={`section-${index}`} className="scroll-mt-28">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="mr-2 text-primary-500">{String(index + 1).padStart(2, '0')}</span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph.slice(0, 24)} className="text-sm leading-loose text-foreground-700 md:text-base">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {section.list.length > 0 && (
                      <ul className="mt-5 space-y-2.5 rounded-lg border border-background-200 bg-background-100 p-5">
                        {section.list.map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                            <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-2 border-t border-background-200 pt-6">
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="lg:sticky lg:top-28 lg:h-fit">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-semibold tracking-wide text-foreground-950">本页目录</h2>
                <ol className="mt-4 space-y-3">
                  {article.content.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#section-${index}`}
                        className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                      >
                        <span className="font-heading text-xs text-foreground-400">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        {section.heading}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">准备好投递了吗</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  材料整理好之后，到平台导航大全里按类别挑两到三个匹配渠道即可。
                </p>
                <Link
                  to="/directory"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-500 cursor-pointer"
                >
                  查看平台导航<i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>

          <section className="mt-16">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续阅读</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          </section>
        </div>
      </article>
    </SiteLayout>
  );
}